/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { Observable, of } from 'rxjs'
import { take } from 'rxjs/operators'
import { MyProfileAddressListService } from '../../services/myprofile-address-list.service'
import { AddressDto } from '../../../../../../apps/myprofile-ui/src/app/services/address/address.service'

@Component({
  selector: 'myprofile-address-list-checkout',
  templateUrl: './myprofile-address-list-checkout.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileAddressListCheckoutComponent implements OnInit {
  private myProfileAddressListService = inject(MyProfileAddressListService)
  private _translateService = inject(TranslateService)
  private store = inject(Store)

  @Input() instanceId?: string

  @Input() emptyListLabel?: string
  @Input() editItemLabel?: string
  @Input() token!: string

  public radioListDisabled$: Observable<boolean> = of(false)
  public addressIdBeingEdited$: Observable<string | null> = of(null)
  public addresses$: Observable<AddressDto[]> = of([])
  public hasAddresses$: Observable<boolean> = of(false)
  public selectedAddressId$: Observable<string | null> = of(null)

  public ngOnInit() {
    this.addresses$ = this.store.select(({ addressWidget }) => addressWidget.addresses)
    this.hasAddresses$ = this.store.select(({ addressWidget }) => addressWidget.addresses.length)

    this.radioListDisabled$ = this.store.select(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId as string].addressList.disabled,
    )

    this.selectedAddressId$ = this.store.select(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId as string].addressList.selectedAddressId,
    )

    this.addressIdBeingEdited$ = this.store.select(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId as string].addressIdBeingEdited,
    )
  }

  public async selectItem(itemId: string) {
    this.myProfileAddressListService.selectItem(itemId, this.instanceId as string)
  }

  public editItem(item: any) {
    this.radioListDisabled$.pipe(take(1)).subscribe((disabled) => {
      if (!disabled) {
        this.myProfileAddressListService.editItem(item, this.instanceId as string)
      }
    })
  }

  public getUsageString(usage: 'home' | 'work') {
    const usageKeys = {
      work: 'pidpaw.editAddress.radio.addressTypeWork.label',
      home: 'pidpaw.editAddress.radio.addressTypeHome.label',
    }

    return this._translateService.instant(usageKeys[usage])
  }

  public getPrefixString(isStandard: boolean) {
    const translatedString = this._translateService.instant('pidpaw.selectAddress.text.preferredAddress')
    return isStandard ? ` | ${translatedString}` : ''
  }

  public editButtonAriaLabel(item: AddressDto): string {
    return this.myProfileAddressListService.editButtonAriaLabel(item)
  }
}
